// Shared banner chrome for the composer's inline notices (view-only,
// can't-email, send-error) — same padding/radius/font-size, differing only
// in tone and accessible role (repo convention: "when 3+ components share
// the same structure, extract a parameterized shared component"). Also
// exports `ComposerErrorBanner`, the per-mode send-failure banner (AI-1350).
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import type {
  InboxErrorKind,
  UseInboxActionsResult,
} from '../ThreadPane/useInboxActions'
import { actionFailure } from '../ThreadPane/useInboxActions'

export interface ComposerBannerProps {
  tone: 'neutral' | 'error'
  role: 'note' | 'alert'
  children: React.ReactNode
}

const ComposerBanner = ({ tone, role, children }: ComposerBannerProps) => {
  const [css] = useStyletron()
  const colors =
    tone === 'error'
      ? { backgroundColor: palette.cayenne05, color: palette.cayenne50 }
      : { backgroundColor: palette.grey05, color: palette.grey70 }

  return (
    <div
      role={role}
      className={css({
        padding: '10px 12px',
        borderRadius: '8px',
        fontSize: '13px',
        ...colors,
      })}
    >
      {children}
    </div>
  )
}

export default ComposerBanner

const t = buildTranslate('feedback_inbox.composer')

// Per-mode send-failure copy (S4 §3.2 F2–F9); network also covers 5xx.
const COMPOSER_ERROR_KEYS: Record<InboxErrorKind, string> = {
  network: 'error_banner',
  validation: 'error_banner_validation',
  permission: 'error_banner_permission',
}

// Renders only for a failed Send; header-control failures belong to
// ThreadPane's header banner.
export const ComposerErrorBanner = ({
  actions,
}: {
  actions: Pick<UseInboxActionsResult, 'error' | 'failure'>
}) => {
  const failure = actionFailure(actions)
  if (failure?.action !== 'send') return null
  return (
    <ComposerBanner tone="error" role="alert">
      {t(COMPOSER_ERROR_KEYS[failure.kind])}
    </ComposerBanner>
  )
}
